import { PropertyCardComponent } from "@/components/property-card/property-card.component";
import { AlertService } from "@/services/alert.service";
import { NgElement, WithProperties } from "@angular/elements";
import {
  Location,
  PropertiesService,
  RealProperty,
  RealPropertySummaryLocation,
} from "@eemo/api-client";
import * as L from "leaflet";
import "leaflet-providers";
import "leaflet.icon.glyph";
import "leaflet.locatecontrol";
import "leaflet.markercluster";
import { Observable, Subject, Subscription } from "rxjs";
import { LayerType } from "../enums/LayerType.enum";
import {
  parcelleHoverStyle,
  parcelleSelectedHoverStyle,
  parcelleSelectedStyle,
  parcelleStyle,
} from "../functions/parcellesStyle";
import { LayerControl } from "../interfaces/layer-controls.interface";
import { MapParcelle } from "../interfaces/mapParcelle.interface";
import vectorTileLayer from "../leaflet-vector-tile-fork/VectorTileLayer";

export class MapService {
  htmlId: string;
  map: L.Map;
  cadastreVectorTileLayer: any;
  markerAddress: L.Marker;
  markerCluster: L.MarkerClusterGroup;
  locationUpdated$ = new Subject<Location>();
  parcelleSelected$ = new Subject<MapParcelle>();
  parcelleUnSelected$ = new Subject<MapParcelle>();
  currentControls: L.Control.LayersObject; //
  _parcelleInputSelected$: Observable<string>;
  _parcelleInputUnSelected$: Observable<string>;
  _parcelleInputSelectedSub = new Subscription();
  _parcelleInputUnSelectedSub = new Subscription();
  landRegistriesSelectedMax: number | null = null;

  readonly SATELLITE_URL =
    "https://data.geopf.fr//wmts?service=WMTS&request=GetTile&version=1.0.0&tilematrixset=PM&tilematrix={z}&tilecol={x}&tilerow={y}&layer=ORTHOIMAGERY.ORTHOPHOTOS&format=image/jpeg&style=normal";
  readonly PLAN_URL =
    "https://data.geopf.fr//wmts?service=WMTS&request=GetTile&version=1.0.0&tilematrixset=PM&tilematrix={z}&tilecol={x}&tilerow={y}&layer=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&format=image/png&style=normal";
  readonly CADASTRE_URL =
    "https://data.geopf.fr//wmts?service=WMTS&request=GetTile&version=1.0.0&tilematrixset=PM&tilematrix={z}&tilecol={x}&tilerow={y}&layer=CADASTRALPARCELS.PARCELLAIRE_EXPRESS&format=image/png&style=normal";
  readonly CADASTRE_DATA =
    "https://openmaptiles.geo.data.gouv.fr/data/cadastre/{z}/{x}/{y}.pbf";

  readonly CONTROL_TEXT_PLAN = "Plan";
  readonly CONTROL_TEXT_SATELLITE = "Satellite";
  readonly CONTROL_TEXT_CADASTRE = "Cadastre";
  readonly CONTROL_TEXT_UNKNONWN = "INCONNU";

  readonly ATTRIBUTION_PLAN = "IGN";
  readonly ATTRIBUTION_SATELLITE = "IGN";
  readonly ATTRIBUTION_CADASTRE = "IGN";

  selectParcelle: { [key: string]: boolean } = {};
  parcelleIdToFeatureId: { [key: string]: string } = {};
  hasEventParcelles: boolean;

  constructor(
    private propertyService: PropertiesService,
    private alertService: AlertService,
    htmlId: string
  ) {
    this.htmlId = htmlId;
  }

  set parcelleInputSelected$(obs) {
    this._parcelleInputSelectedSub.unsubscribe();
    this._parcelleInputSelected$ = obs;
    this._parcelleInputSelectedSub = this._parcelleInputSelected$.subscribe(
      (pId) => {
        this.updateParcelleInputSelect(pId);
      }
    );
  }
  set parcelleInputUnSelected$(obs) {
    this._parcelleInputUnSelectedSub.unsubscribe();
    this._parcelleInputUnSelected$ = obs;
    this._parcelleInputUnSelectedSub = this._parcelleInputUnSelected$.subscribe(
      (pId) => this.updateParcelleInputUnSelect(pId)
    );
  }

  getDefaultParcelleStyle() {
    return parcelleStyle;
  }

  getParcelleSelectedStyle() {
    return parcelleSelectedStyle;
  }

  getParcelleUnSelectedStyle() {
    return parcelleStyle;
  }

  getParcelleHoverStyle() {
    return parcelleHoverStyle;
  }

  getParcelleSelectedHoverStyle() {
    return parcelleSelectedHoverStyle;
  }

  init(
    longitude: number,
    latitude: number,
    defaultZoom,
    landRegistriesSelectedMax: number | null
  ) {
    this.landRegistriesSelectedMax = landRegistriesSelectedMax;
    this.map = L.map(this.htmlId).setView(
      { lng: longitude, lat: latitude },
      defaultZoom
    );

    /*this.map.on('zoomend', function (e) {
      console.log('zoom->', e.target._zoom);
    });*/
  }

  clear() {
    this.map.remove();
  }

  isInitialized(): boolean {
    return this.map ? true : false;
  }

  generateSatelliteLayer(): L.TileLayer {
    // layer satellite
    const satellite: L.TileLayer = L.tileLayer(this.SATELLITE_URL, {
      id: "satellite",
      attribution: this.ATTRIBUTION_SATELLITE,
      zoomOffset: 0,
      zIndex: 3,
      maxZoom: 19,
      minZoom: 10,
    });
    return satellite;
  }

  generatePlanLayer(): L.TileLayer {
    // layer IGN
    const plan = L.tileLayer(this.PLAN_URL, {
      id: "plan",
      attribution: this.ATTRIBUTION_PLAN,
      zIndex: 3,
      maxZoom: 19,
    });
    return plan;
  }

  generateCadastreLayer(): L.TileLayer {
    // layer CADASTRE
    const cadastre = L.tileLayer(this.CADASTRE_URL, {
      id: "cadastre",
      attribution: this.ATTRIBUTION_CADASTRE,
      zIndex: 3,
      maxZoom: 19,
    });
    return cadastre;
  }

  setLayer(layer: L.TileLayer) {
    this.map.addLayer(layer);
  }

  addZoomControls() {
    L.control.scale({ imperial: false, metric: true }).addTo(this.map);
  }

  addLocation() {
    const locateOptions = {
      flyTo: true,
      keepCurrentZoomLevel: true,
      icon: "fa fa-location-arrow",
      onLocationError: this.onLocationError.bind(this),
      enableHighAccuracy: true,
    };
    L.control.locate(locateOptions).addTo(this.map);
  }

  addLayerControls(layers: LayerControl[]) {
    // ajout controles
    this.currentControls = {};
    layers.forEach((l) => {
      this.currentControls[l.name] = l.layer;
    });
    L.control.layers(this.currentControls).addTo(this.map);
  }

  // AJOUT PARCELLES VECTORIELLES
  addParcelleVectorLayer() {
    const openmaptilesCadastreUrl = this.CADASTRE_DATA;
    this.cadastreVectorTileLayer = vectorTileLayer(openmaptilesCadastreUrl, {
      interactive: this.hasEventParcelles,
      maxDetailZoom: 16,
      maxZoom: 19,
      minZoom: 17,
      className: "parcelle-vector-layer",
      style: this.getDefaultParcelleStyle(),
      zIndex: 10,
      getFeatureId: function (feat) {
        return feat.properties.id;
      },
    });

    this.cadastreVectorTileLayer.on("click", (e) => {
      const property: MapParcelle | null = this.getPropertyByEvent(e);
      if (this.isParcelleSelected(property.id)) {
        //unselect
        this.updateParcelleInputUnSelect(property.id);
        this.parcelleUnSelected$.next(property);
      } else {
        //select
        if (
          this.landRegistriesSelectedMax === null ||
          this.getNbParcelleSelected() < this.landRegistriesSelectedMax
        ) {
          this.updateParcelleInputSelect(property.id);
          this.parcelleSelected$.next(property);
        }
      }
    });

    this.cadastreVectorTileLayer.on("mouseover", (e) => {
      const property: MapParcelle | null = this.getPropertyByEvent(e);
      const parcelleStyle = this.isParcelleSelected(property.id)
        ? this.getParcelleSelectedHoverStyle()
        : this.getParcelleHoverStyle();
      this.cadastreVectorTileLayer.setFeatureStyle(property.id, parcelleStyle);
    });

    this.cadastreVectorTileLayer.on("mouseout", (e) => {
      const property: MapParcelle | null = this.getPropertyByEvent(e);
      const parcelleStyle = this.isParcelleSelected(property.id)
        ? this.getParcelleSelectedStyle()
        : this.getParcelleUnSelectedStyle();
      this.cadastreVectorTileLayer.setFeatureStyle(property.id, parcelleStyle);
    });

    this.cadastreVectorTileLayer.addTo(this.map);
  }

  updateParcelleInputSelect(parcelleId: string) {
    this.selectParcelle[parcelleId] = true;
    this.cadastreVectorTileLayer.setFeatureStyle(
      parcelleId,
      this.getParcelleSelectedStyle()
    );
  }

  updateParcelleInputUnSelect(parcelleId: string) {
    this.selectParcelle[parcelleId] = false;
    this.cadastreVectorTileLayer.setFeatureStyle(
      parcelleId,
      this.getParcelleUnSelectedStyle()
    );
  }

  mapLayerControlFromLayer(type: LayerType, layer: L.TileLayer): LayerControl {
    let name = this.CONTROL_TEXT_UNKNONWN;
    switch (type) {
      case LayerType.PLAN:
        name = this.CONTROL_TEXT_PLAN;
        break;
      case LayerType.SATELLITE:
        name = this.CONTROL_TEXT_SATELLITE;
        break;
      case LayerType.CADASTRE:
        name = this.CONTROL_TEXT_CADASTRE;
        break;
      default:
        break;
    }
    return {
      name: name,
      layer: layer,
    };
  }

  setDefaultLayer(
    defaultLayer: LayerType,
    layerType: LayerType,
    layer: L.TileLayer
  ) {
    if (layerType === defaultLayer) {
      this.setLayer(layer);
    }
  }

  private onLocationError() {
    this.alertService.warning(
      "La géolocalisation a échoué. Assurez-vous d'avoir validé l'autorisation demandée."
    );
  }

  addMarkers(
    props: RealProperty[] | RealPropertySummaryLocation[],
    propsType: "full" | "summary",
    updateLocationEnabled: boolean,
    showPopup: boolean
  ) {
    const shadowUrl = "assets/map/marker-shadow.png";
    const iconDefault = L.icon({
      iconRetinaUrl: "assets/map/marker-icon-2x-red.png",
      iconUrl: "assets/map/marker-icon-red.png",
      shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41],
    });

    const iconCertified = L.icon({
      iconRetinaUrl: "assets/map/marker-icon-2x.png",
      iconUrl: "assets/map/marker-icon.png",
      shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41],
    });

    const iconDraggable = L.icon.glyph({
      prefix: "fas",
      glyph: "arrows-alt",
      glyphSize: "14px",
    });

    let curIcon: any;
    this.markerCluster = new window.L.MarkerClusterGroup({
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: true,
      zoomToBoundsOnClick: true,
      removeOutsideVisibleBounds: true,
    });

    // TODO
    props.forEach((prop) => {
      if (prop.location) {
        curIcon = updateLocationEnabled
          ? iconDraggable
          : prop.certified
          ? iconCertified
          : iconDefault;
        const title = updateLocationEnabled
          ? "Bougez le marqueur pour affiner la localisation"
          : prop.name;

        const marker = L.marker(
          [prop.location.latitude, prop.location.longitude],
          {
            icon: curIcon,
            draggable: updateLocationEnabled,
            autoPan: true,
            title,
          }
        ).on("dragend", (event: L.DragEndEvent) => {
          const position = event.target.getLatLng();
          this.locationUpdated$.next({
            latitude: position.lat,
            longitude: position.lng,
          });
        });

        if (showPopup) {
          marker.bindPopup(
            () => {
              const propCard: NgElement &
                WithProperties<PropertyCardComponent> = document.createElement(
                "property-card-element"
              ) as NgElement & WithProperties<PropertyCardComponent>;
              // Listen to the close event
              propCard.addEventListener("closed", () =>
                document.body.removeChild(propCard)
              );
              propCard.showMapLink = true;
              propCard.showOwnerName = true;
              propCard.smallCard = true;
              if (propsType === "summary") {
                propCard.propSummary = prop;
              } else {
                propCard.prop = prop;
              }
              // Add to the DOM
              document.body.appendChild(propCard);
              return propCard;
            },
            { maxWidth: 385, minWidth: 300 }
          );
        }
        this.markerCluster.addLayer(marker);
      }
    });

    this.map.addLayer(this.markerCluster);
  }

  getPropertyByEvent(event: any): MapParcelle | null {
    return event?.layer?.properties || null;
  }

  isParcelleSelected(propertyId): boolean {
    return this.selectParcelle[propertyId] || false;
  }

  getNbParcelleSelected(): number {
    let nb = 0;
    for (const key in this.selectParcelle) {
      if (this.selectParcelle[key]) {
        nb = nb + 1;
      }
    }
    return nb;
  }
}
